import { useQuery } from '@tanstack/react-query'
import { legalFoundationService } from '@/lib/api/services/legal-foundation'
import type { ApiResponse, LegalFoundation, LegalFoundationFilterParams, PaginatedResponse, PaginationParams } from '@/lib/api/types'

export const legalFoundationKeys = {
  all: ['legal-foundations'] as const,
  published: () => [...legalFoundationKeys.all, 'published'] as const,
  search: (searchTerm: string, params: PaginationParams) =>
    [...legalFoundationKeys.all, 'search', searchTerm, params] as const,
  filter: (filters: LegalFoundationFilterParams, params: PaginationParams) =>
    [...legalFoundationKeys.all, 'filter', filters, params] as const,
}

/**
 * Hook to fetch all published legal foundations
 */
export const usePublishedLegalFoundations = () => {
  return useQuery<ApiResponse<LegalFoundation[]>, Error>({
    queryKey: legalFoundationKeys.published(),
    queryFn: () => legalFoundationService.getPublishedLegalFoundations(),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch legal foundations based on search query
 * @param searchTerm The search term to query legal foundations
 * @param params The pagination parameters
 */

export const useSearchLegalFoundations = (searchTerm: string, params: PaginationParams) => {
  return useQuery<ApiResponse<PaginatedResponse<LegalFoundation>>, Error>({
    queryKey: legalFoundationKeys.search(searchTerm, params),
    queryFn: () => legalFoundationService.searchLegalFoundations(searchTerm, params),
    enabled: searchTerm.trim().length > 0, // Only run this query if there's a search term
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch legal foundations based on filter criteria
 * @param filters The filter criteria to query legal foundations
 * @param params The pagination parameters
 */

export const useFilterLegalFoundations = (filters: LegalFoundationFilterParams, params: PaginationParams, enabled: boolean) => {
  return useQuery<ApiResponse<PaginatedResponse<LegalFoundation>>, Error>({
    queryKey: legalFoundationKeys.filter(filters, params),
    queryFn: () => legalFoundationService.filterLegalFoundations(filters, params),
    enabled: enabled, 
    staleTime: 5 * 60 * 1000,
  })
}
